import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useConvenios, useConvenioContracts, useAfianzadoras } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Button } from '../../components/button';
import { Badge } from '../../components/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { ContratoView } from '../../components/ContratoView';
import { FianzaView } from '../../components/FianzaView';
import SearchableListbox from '../../components/SearchableListbox';
import ConvenioHeader from '../../components/convenio/ConvenioHeader';
import ConvenioResumen from '../../components/convenio/ConvenioResumen';
import ProveedoresChaseList from '../../components/convenio/ProveedoresChaseList';
import ContratoFianzasPanel from '../../components/convenio/ContratoFianzasPanel';
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from '../../hooks/useFrozenColumns';
import {
  aggregateProveedores,
  computeEmittedFianzas,
  computePendingFianzas,
  dueLabel,
  getFianzaPorcentaje,
  getFianzaStatus,
} from '../../utils/convenioCompliance';
import { ESTADO_FIANZA } from '../../utils/fianzaEventos';
import { ChevronDownIcon, ChevronRightIcon, FolderIcon } from '@heroicons/react/20/solid';

// Same tab order the broker's ConvenioDetails uses, minus the broker-only tabs
// (proyectos, comisiones, configuración). Resumen is the landing tab.
const TABS = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'contratos', label: 'Contratos' },
  { key: 'fianzas', label: 'Fianzas' },
  { key: 'proyectos', label: 'Proyectos' },
  { key: 'proveedores', label: 'Proveedores' },
];
const TAB_KEYS = new Set(TABS.map((t) => t.key));

const formatDate = (ts) => {
  if (!ts) return '-';
  const ms = ts?.toMillis?.() || ts;
  return new Date(ms).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMonto = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

// Case- and accent-insensitive matching for the tab search boxes.
const normalizar = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const FIANZA_ESTATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estatus' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'vencida', label: 'Vencida' },
  { value: 'pendiente', label: 'Por iniciar' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'anulada', label: 'Anulada' },
  { value: 'sustituida', label: 'Sustituida' },
];

const FIANZA_VIGENCIA_OPTIONS = [
  { value: 'all', label: 'Cualquier vigencia' },
  { value: 'v30', label: 'Vence en ≤ 30 días' },
  { value: 'v90', label: 'Vence en ≤ 90 días' },
  { value: 'vencida', label: 'Vigencia terminada' },
];

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const ClientPortalConvenio = () => {
  const { convenioId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { conveniosById, conveniosLoading } = useConvenios();
  const contracts = useConvenioContracts(convenioId);
  const { afianzadorasById } = useAfianzadoras();
  const { userData } = useUser();

  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [selectedFianzaContrato, setSelectedFianzaContrato] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  // ?proveedor=<agg key> deep-links (from the portal reportes) land on the
  // Proveedores tab with that card expanded and scrolled into view.
  const [expandKey, setExpandKey] = useState(() => searchParams.get('proveedor') || null);
  const [expandedContratoIds, setExpandedContratoIds] = useState(() => new Set());
  const [contratosSearch, setContratosSearch] = useState('');
  const [proveedorFilter, setProveedorFilter] = useState('all');
  const [beneficiarioFilter, setBeneficiarioFilter] = useState('all');
  const [fianzasSearch, setFianzasSearch] = useState('');
  const [fianzasProveedor, setFianzasProveedor] = useState('all');
  const [fianzasEstatus, setFianzasEstatus] = useState('all');
  const [fianzasVigencia, setFianzasVigencia] = useState('all');

  const convenio = conveniosById[convenioId];
  const beneficiarios = useMemo(() => convenio?.beneficiarios || [], [convenio?.beneficiarios]);
  const proyectos = useMemo(() => convenio?.proyectos || [], [convenio?.proyectos]);

  const beneficiariosById = useMemo(() => {
    const map = {};
    beneficiarios.forEach((b) => { map[b.id] = b; });
    return map;
  }, [beneficiarios]);

  const proyectosById = useMemo(() => {
    const map = {};
    proyectos.forEach((p) => { map[p.id] = p; });
    return map;
  }, [proyectos]);

  const accessibleContracts = useMemo(() => {
    const access = userData?.convenioAccess?.[convenioId];
    if (!access) return [];
    if (access.fullAccess) return contracts;
    const proyectoIds = new Set(access.proyectoIds || []);
    return contracts.filter((c) => c.proyectoId && proyectoIds.has(c.proyectoId));
  }, [contracts, userData?.convenioAccess, convenioId]);

  // Proyecto-scoped users only see their proyectos; fullAccess sees them all.
  const accessibleProyectos = useMemo(() => {
    const access = userData?.convenioAccess?.[convenioId];
    if (!access) return [];
    if (access.fullAccess) return proyectos;
    const ids = new Set(access.proyectoIds || []);
    return proyectos.filter((p) => ids.has(p.id));
  }, [proyectos, userData?.convenioAccess, convenioId]);

  // Tab — with backward compat: unknown / dropped tabs fall back to resumen
  const rawTab = searchParams.get('tab');
  const activeTab = TAB_KEYS.has(rawTab) ? rawTab : 'resumen';
  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const contratosFrozen = useFrozenColumns(2, activeTab);

  const proveedoresAgg = useMemo(
    () => aggregateProveedores(accessibleContracts, beneficiariosById, proyectosById),
    [accessibleContracts, beneficiariosById, proyectosById]
  );
  const allFianzas = useMemo(
    () => computeEmittedFianzas(accessibleContracts, beneficiariosById, proyectosById),
    [accessibleContracts, beneficiariosById, proyectosById]
  );

  const openContrato = (contractId) => {
    const contract = accessibleContracts.find((c) => c.id === contractId);
    if (contract) {
      setSelectedContrato(contract);
      setIsContratoViewOpen(true);
    }
  };

  const openFianza = (fianza) => {
    const contract = accessibleContracts.find((c) => c.id === fianza.contractId);
    if (!contract) return;
    setSelectedFianzaContrato(contract);
    setSelectedFianza({ ...fianza, contratoId: contract.id });
    setIsFianzaViewOpen(true);
  };

  const openFianzaFromPanel = (contratoId, fianza) => {
    const contract = accessibleContracts.find((c) => c.id === contratoId);
    if (!contract) return;
    setSelectedFianzaContrato(contract);
    setSelectedFianza({ ...fianza, contratoId: contract.id });
    setIsFianzaViewOpen(true);
  };

  const handleProveedorClickFromResumen = (key) => {
    setExpandKey(key);
    setActiveTab('proveedores');
  };

  // Client-safe subset of the rollup ConvenioDetails builds per contrato:
  // lifecycle counts + monto afianzado only. Prima, pago status and endosos are
  // the fiado's commercial data and must not cross to the beneficiario's side —
  // ContratoFianzasPanel in mode='client' never renders them, so the rows only
  // need placeholders there.
  const getContratoFianzaStats = (contrato) => {
    const now = Date.now();
    const fianzas = contrato.fianzas || [];
    const r = {
      emitidas: fianzas.length,
      vigentes: 0,
      porVencer: 0,
      vencidas: 0,
      porIniciar: 0,
      terminadas: 0,
      montoAfianzado: 0,
      pendientes: computePendingFianzas([contrato]),
      rows: [],
    };
    r.reqPendientes = r.pendientes.length;
    fianzas.forEach((fianza) => {
      const status = getFianzaStatus(fianza, now);
      const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
      if (status.estado === ESTADO_FIANZA.ACTIVA) {
        r.montoAfianzado += Number(fianza.montoAfianzado) || 0;
        if (status.code === 'vigente') {
          r.vigentes += 1;
          if (fin > 0 && fin - now <= THIRTY_DAYS) r.porVencer += 1;
        } else if (status.code === 'vencida') {
          r.vencidas += 1;
        } else {
          r.porIniciar += 1;
        }
      } else {
        r.terminadas += 1;
      }
      r.rows.push({
        fianza: { ...fianza, porcentajeInfo: getFianzaPorcentaje(fianza, contrato) },
        status,
        prima: 0,
        pagoEstado: 'pendiente',
        pagadas: 0,
        facturasTotal: 0,
        endososPendientes: 0,
      });
    });
    return r;
  };

  const toggleContratoExpanded = (contratoId) => {
    setExpandedContratoIds((prev) => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  };

  if (conveniosLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  if (!convenio) {
    return (
      <div className="text-center py-12">
        <Heading>Convenio no encontrado</Heading>
        <Button className="mt-4" onClick={() => navigate('/app/portal/convenios')}>
          Volver
        </Button>
      </div>
    );
  }

  const renderContratos = () => {
    const query = contratosSearch.trim().toLowerCase();
    const filteredContracts = accessibleContracts.filter((c) => {
      if (query && !(c.numeroContrato?.value || '').toString().toLowerCase().includes(query)) {
        return false;
      }
      if (proveedorFilter !== 'all' && (c.proveedor?.value || '') !== proveedorFilter) {
        return false;
      }
      if (beneficiarioFilter !== 'all' && (c.contratante?.value || '') !== beneficiarioFilter) {
        return false;
      }
      return true;
    });

    const uniqueSorted = (getter) => {
      const set = new Set();
      accessibleContracts.forEach((c) => {
        const v = getter(c);
        if (v) set.add(v);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    };
    const proveedorOptions = [
      { value: 'all', label: 'Todos los proveedores' },
      ...uniqueSorted((c) => c.proveedor?.value).map((v) => ({ value: v, label: v })),
    ];
    const beneficiarioOptions = [
      { value: 'all', label: 'Todos los beneficiarios' },
      ...uniqueSorted((c) => c.contratante?.value).map((v) => ({ value: v, label: v })),
    ];

    return (
      <div className="space-y-4">
        {accessibleContracts.length === 0 ? (
          <div className="text-center py-8">
            <Text>No hay contratos disponibles.</Text>
          </div>
        ) : (
          <>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
            <input
              type="text"
              value={contratosSearch}
              onChange={(e) => setContratosSearch(e.target.value)}
              placeholder="Buscar por No. de Contrato..."
              className="w-full sm:max-w-xs px-3 py-2 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
            />
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Proveedor:</label>
              <SearchableListbox
                value={proveedorFilter}
                onChange={setProveedorFilter}
                options={proveedorOptions}
                className="min-w-[220px] max-w-[260px]"
                placeholder="Selecciona proveedor"
                searchPlaceholder="Buscar proveedor..."
                popupClassName="w-[260px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Beneficiario:</label>
              <SearchableListbox
                value={beneficiarioFilter}
                onChange={setBeneficiarioFilter}
                options={beneficiarioOptions}
                className="min-w-[220px] max-w-[260px]"
                placeholder="Selecciona beneficiario"
                searchPlaceholder="Buscar beneficiario..."
                popupClassName="w-[260px]"
              />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <div className="relative">
              <Table onScroll={contratosFrozen.onScroll}>
                <TableHead className="bg-azul-surelia">
                  <TableRow>
                    <TableHeader
                      ref={contratosFrozen.setRef(0)}
                      style={{ left: contratosFrozen.offsets[0] }}
                      className={`text-white font-semibold py-3 border-b-0 w-8 ${frozenHeadClass}`}
                    ></TableHeader>
                    <TableHeader
                      ref={contratosFrozen.setRef(1)}
                      style={{ left: contratosFrozen.offsets[1] }}
                      className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                    >No. de Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Fianzas</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Proveedor</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Beneficiario</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Proyecto</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Monto Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Afianzado</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContracts.map((contrato, index) => {
                    const fianzaStats = getContratoFianzaStats(contrato);
                    const expanded = expandedContratoIds.has(contrato.id);
                    return (
                      <React.Fragment key={contrato.id}>
                      <TableRow
                        className={`group cursor-pointer transition-colors duration-200 hover:bg-azul-surelia/5 ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                        onClick={() => openContrato(contrato.id)}
                      >
                        <TableCell
                          style={{ left: contratosFrozen.offsets[0] }}
                          className={`py-3 pr-0 ${frozenCellClass(index)}`}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleContratoExpanded(contrato.id);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title={expanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                          >
                            {expanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell
                          style={{ left: contratosFrozen.offsets[1] }}
                          className={`py-3 ${frozenCellClass(index)}`}
                        >
                          <Text className="text-sm font-semibold text-zinc-900 truncate max-w-xs" title={contrato.numeroContrato?.value || '-'}>
                            {contrato.numeroContrato?.value || '-'}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3">
                          {fianzaStats.emitidas === 0 && fianzaStats.reqPendientes === 0 ? (
                            <Text className="text-sm text-zinc-400">Sin fianzas</Text>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <div className="flex flex-wrap items-center gap-1">
                                {fianzaStats.vigentes > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    {fianzaStats.vigentes} vig.
                                  </span>
                                )}
                                {fianzaStats.porVencer > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                    {fianzaStats.porVencer} por vencer
                                  </span>
                                )}
                                {fianzaStats.vencidas > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                    {fianzaStats.vencidas} venc.
                                  </span>
                                )}
                                {fianzaStats.porIniciar > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                    {fianzaStats.porIniciar} por iniciar
                                  </span>
                                )}
                                {fianzaStats.terminadas > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">
                                    {fianzaStats.terminadas} term.
                                  </span>
                                )}
                              </div>
                              {fianzaStats.reqPendientes > 0 && (
                                <Text className="text-xs font-medium text-orange-600">
                                  {fianzaStats.reqPendientes} por emitir
                                </Text>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm">{contrato.proveedor?.value || '-'}</TableCell>
                        <TableCell className="py-3 text-sm">{contrato.contratante?.value || '-'}</TableCell>
                        <TableCell className="py-3 text-sm">{proyectosById[contrato.proyectoId]?.nombre || '-'}</TableCell>
                        <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                          {formatMonto(contrato.montoContrato?.value || 0)}
                        </TableCell>
                        <TableCell className="py-3">
                          {fianzaStats.montoAfianzado > 0 ? (
                            <Text className="text-xs text-zinc-700 font-semibold tabular-nums">
                              {formatMonto(fianzaStats.montoAfianzado)}
                            </Text>
                          ) : (
                            <Text className="text-sm text-zinc-400">—</Text>
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          {contrato.status === 'procesando' ? (
                            <span className="px-3 py-1 inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-yellow-700" />
                              Procesando
                            </span>
                          ) : (
                            <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                              contrato.estatus?.value === 'activo' ? 'bg-green-100 text-green-800' :
                              contrato.estatus?.value === 'completado' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {contrato.estatus?.value || contrato.status || 'N/A'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="bg-azul-surelia/5">
                          {/* Spacer over the frozen columns. The panel is sticky, and at
                              the end of the scroll its containing block shoves it
                              back over the frozen strip — this opaque z-10 cell is
                              what it slides UNDER, instead of crossing the freeze
                              divider. Tint = bg-azul-surelia/5 flattened over white. */}
                          <TableCell colSpan={2} style={{ left: 0 }} className="sticky z-10 bg-[#F4F6F7] p-0" />
                          <TableCell colSpan={7} className="py-3 px-6">
                            <ContratoFianzasPanel
                              stats={fianzaStats}
                              contratoId={contrato.id}
                              onFianzaClick={openFianzaFromPanel}
                              canUpload={false}
                              mode="client"
                              stickyOffset={contratosFrozen.total}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
                  pinned to the boundary instead of riding along with the cells. */}
              {contratosFrozen.scrolled && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
                  style={{ left: contratosFrozen.dividerLeft, bottom: contratosFrozen.dividerBottom }}
                />
              )}
            </div>
            {filteredContracts.length === 0 && (
              <div className="text-center py-6 text-sm text-gray-500">
                No hay contratos que coincidan con los filtros.
              </div>
            )}
          </div>
          </>
        )}
      </div>
    );
  };

  const renderFianzasEmitidas = () => {
    const now = Date.now();
    const proveedorSet = new Set();
    allFianzas.forEach((f) => { if (f.contractProveedor) proveedorSet.add(f.contractProveedor); });
    const fianzasProveedorOptions = [
      { value: 'all', label: 'Todos los proveedores' },
      ...Array.from(proveedorSet).sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v })),
    ];

    // Requisitos still without a fianza, shown in their own table below the
    // emitidas: the estatus/vigencia filters are about an issued fianza, so
    // only search and proveedor apply here.
    const pendientesAll = computePendingFianzas(accessibleContracts, beneficiariosById, proyectosById);

    const q = normalizar(fianzasSearch.trim());
    const filteredFianzas = allFianzas.filter((f) => {
      const status = getFianzaStatus(f, now);
      if (fianzasEstatus !== 'all' && status.code !== fianzasEstatus) return false;
      if (fianzasProveedor !== 'all' && (f.contractProveedor || '') !== fianzasProveedor) return false;
      if (fianzasVigencia !== 'all') {
        const fin = f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0;
        if (fianzasVigencia === 'vencida') {
          if (!(fin && fin < now)) return false;
        } else {
          const dias = fianzasVigencia === 'v30' ? 30 : 90;
          if (!(fin > now && fin <= now + dias * 86400000)) return false;
        }
      }
      if (q) {
        const campos = [
          f.numeroFianza, f.tipoFianza || f.tipo, f.contractNumero, f.contractTitle,
          f.contractProveedor, f.proyectoNombre, f.beneficiarioNombre,
        ];
        if (!campos.some((c) => normalizar(c).includes(q))) return false;
      }
      return true;
    });

    const filteredPendientes = pendientesAll.filter((fr) => {
      if (fianzasProveedor !== 'all' && (fr.fiadoNombre || '') !== fianzasProveedor) return false;
      if (q) {
        const campos = [fr.tipo, fr.contractNumero, fr.contractTitle, fr.fiadoNombre, fr.proyectoNombre, fr.beneficiarioNombre];
        if (!campos.some((c) => normalizar(c).includes(q))) return false;
      }
      return true;
    });

    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <input
            type="text"
            value={fianzasSearch}
            onChange={(e) => setFianzasSearch(e.target.value)}
            placeholder="Buscar por número, contrato, proveedor..."
            className="w-full sm:max-w-xs px-3 py-2 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
          />
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Proveedor:</label>
            <SearchableListbox
              value={fianzasProveedor}
              onChange={setFianzasProveedor}
              options={fianzasProveedorOptions}
              className="min-w-[220px] max-w-[260px]"
              placeholder="Selecciona proveedor"
              searchPlaceholder="Buscar proveedor..."
              popupClassName="w-[260px]"
            />
          </div>
          <select
            value={fianzasEstatus}
            onChange={(e) => setFianzasEstatus(e.target.value)}
            className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
          >
            {FIANZA_ESTATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={fianzasVigencia}
            onChange={(e) => setFianzasVigencia(e.target.value)}
            className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
          >
            {FIANZA_VIGENCIA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {filteredFianzas.length !== allFianzas.length && (
            <Text className="text-xs text-zinc-500">
              {filteredFianzas.length} de {allFianzas.length} fianzas
            </Text>
          )}
        </div>
        <Heading level={4} className="text-azul-surelia">Emitidas</Heading>
        {filteredFianzas.length === 0 ? (
          <div className="bg-white rounded-lg border border-azul-surelia/15 py-6 text-center">
            <Text className="text-sm text-zinc-500">
              {allFianzas.length === 0
                ? 'Aún no se ha emitido ninguna fianza en este convenio.'
                : 'No hay fianzas que coincidan con los filtros.'}
            </Text>
          </div>
        ) : (
        <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                <TableRow>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">No. Fianza</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Proveedor</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Proyecto</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Monto</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Vigencia</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFianzas.map((f, index) => {
                  const status = getFianzaStatus(f, now);
                  return (
                    <TableRow
                      key={f.id}
                      className={`cursor-pointer transition-colors hover:bg-azul-surelia/5 ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                      onClick={() => openFianza(f)}
                    >
                      <TableCell className="py-3 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{f.tipoFianza || f.tipo || '-'}</span>
                          {f.proveedorMismatch && (
                            <span
                              title={`Fiado XML: ${f.proveedorMismatch.fiadoXmlNombre}. Prevalece el proveedor del contrato (${f.proveedorMismatch.contractProveedor}).`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold"
                            >
                              Fiado no coincide
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-3 text-sm font-mono">{f.numeroFianza || '-'}</TableCell>
                      <TableCell className="py-3 text-sm">{f.contractNumero || '-'}</TableCell>
                      <TableCell className="py-3 text-sm">{f.contractProveedor || '-'}</TableCell>
                      <TableCell className="py-3 text-sm">{f.proyectoNombre || '-'}</TableCell>
                      <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                        {formatMonto(f.montoAfianzado || 0)}
                      </TableCell>
                      <TableCell className="py-3 text-xs text-gray-600">
                        {formatDate(f.inicioVigenciaFianza)} — {formatDate(f.finVigenciaFianza)}
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge color={status.color}>{status.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
        )}

        {/* ── Por emitir y futuras ───────────────────────────────────────── */}
        <div className="pt-2">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <Heading level={4} className="text-azul-surelia">Por emitir y futuras</Heading>
            <Text className="text-xs text-zinc-500">
              {filteredPendientes.length} requisito{filteredPendientes.length === 1 ? '' : 's'} sin fianza emitida
            </Text>
          </div>
          {filteredPendientes.length === 0 ? (
            <div className="bg-white rounded-lg border border-azul-surelia/15 py-6 text-center">
              <Text className="text-sm text-zinc-500">
                {pendientesAll.length === 0
                  ? 'Todas las fianzas requeridas de este convenio ya fueron emitidas.'
                  : 'Ningún requisito coincide con los filtros.'}
              </Text>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                    <TableRow>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Estado</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Proveedor</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Proyecto</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">% del contrato</TableHeader>
                      <TableHeader
                        className="text-white font-semibold py-3 border-b-0 text-right"
                        title="Monto que deberá quedar afianzado cuando se emita la fianza."
                      >Por afianzar</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Emisión</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPendientes.map((fr, index) => {
                      const tone = fr.isFutura
                        ? { badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' }
                        : fr.isOverdue
                        ? { badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' }
                        : { badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
                      return (
                        <TableRow
                          key={fr._key}
                          className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                          onClick={() => openContrato(fr.contractId)}
                        >
                          <TableCell className="py-3 text-sm font-medium text-zinc-800">{fr.tipo || 'Fianza'}</TableCell>
                          <TableCell className="py-3">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>{tone.label}</span>
                          </TableCell>
                          <TableCell className="py-3 text-sm">{fr.contractNumero || '-'}</TableCell>
                          <TableCell className="py-3 text-sm">{fr.fiadoNombre || '-'}</TableCell>
                          <TableCell className="py-3 text-sm">{fr.proyectoNombre || '-'}</TableCell>
                          <TableCell className="py-3 text-sm tabular-nums">
                            {Number(fr.porcentaje) > 0 ? `${Number(fr.porcentaje)}%` : '—'}
                          </TableCell>
                          <TableCell className="py-3 text-sm text-right tabular-nums font-semibold text-azul-surelia">
                            {formatMonto(fr.monto || 0)}
                          </TableCell>
                          <TableCell className={`py-3 text-xs ${tone.due}`}>{dueLabel(fr)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProyectos = () => {
    const contractsByProject = {};
    const unassigned = [];
    accessibleContracts.forEach((c) => {
      if (c.proyectoId) {
        if (!contractsByProject[c.proyectoId]) contractsByProject[c.proyectoId] = [];
        contractsByProject[c.proyectoId].push(c);
      } else {
        unassigned.push(c);
      }
    });

    const proyectoCard = ({ key, nombre, pContracts }) => {
      const agg = {
        contratos: pContracts.length, montoAfianzado: 0,
        vigentes: 0, porVencer: 0, vencidas: 0, reqPendientes: 0,
        rollups: new Map(),
      };
      pContracts.forEach((c) => {
        const r = getContratoFianzaStats(c);
        agg.rollups.set(c.id, r);
        agg.montoAfianzado += r.montoAfianzado;
        agg.vigentes += r.vigentes;
        agg.porVencer += r.porVencer;
        agg.vencidas += r.vencidas;
        agg.reqPendientes += r.reqPendientes;
      });
      return (
        <div key={key} className="bg-white rounded-lg shadow-sm border border-azul-surelia/20 overflow-hidden">
          <div className="px-4 py-3 bg-azul-surelia/5 border-b border-azul-surelia/10 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <FolderIcon className="w-5 h-5 text-azul-surelia/70 shrink-0" />
              <span className="font-semibold text-azul-surelia truncate">{nombre}</span>
              <Badge color="zinc">{agg.contratos} contrato{agg.contratos !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex flex-wrap items-center gap-1">
                {agg.vigentes > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {agg.vigentes} vig.
                  </span>
                )}
                {agg.porVencer > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                    {agg.porVencer} por vencer
                  </span>
                )}
                {agg.vencidas > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    {agg.vencidas} venc.
                  </span>
                )}
                {agg.reqPendientes > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                    {agg.reqPendientes} por emitir
                  </span>
                )}
              </div>
              {agg.montoAfianzado > 0 && (
                <div className="text-right">
                  <div className="text-sm font-bold text-azul-surelia tabular-nums">
                    {formatMonto(agg.montoAfianzado)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-400">afianzado</div>
                </div>
              )}
            </div>
          </div>
          {pContracts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                  <TableRow>
                    <TableHeader className="text-white font-semibold w-8" />
                    <TableHeader className="text-white font-semibold">Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold">Proveedor</TableHeader>
                    <TableHeader className="text-white font-semibold">Monto Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold">Afianzado</TableHeader>
                    <TableHeader className="text-white font-semibold">Fianzas</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pContracts.map((c, i) => {
                    const r = agg.rollups.get(c.id);
                    const cExpanded = expandedContratoIds.has(c.id);
                    return (
                      <React.Fragment key={c.id}>
                      <TableRow
                        className={`cursor-pointer transition-colors hover:bg-azul-surelia/5 ${i % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                        onClick={() => openContrato(c.id)}
                      >
                        <TableCell className="py-3 pr-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleContratoExpanded(c.id);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title={cExpanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                          >
                            {cExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="text-sm font-medium text-azul-surelia">{c.numeroContrato?.value || '-'}</div>
                          {c.tituloContrato?.value && (
                            <div className="text-xs text-zinc-500 truncate max-w-[260px]" title={c.tituloContrato.value}>
                              {c.tituloContrato.value}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm">{c.proveedor?.value || '-'}</TableCell>
                        <TableCell className="py-3 text-sm tabular-nums">{formatMonto(c.montoContrato?.value || 0)}</TableCell>
                        <TableCell className="py-3 text-sm tabular-nums text-azul-surelia font-semibold">
                          {r.montoAfianzado > 0 ? formatMonto(r.montoAfianzado) : '—'}
                        </TableCell>
                        <TableCell className="py-3">
                          {r.emitidas === 0 && r.reqPendientes === 0 ? (
                            <span className="text-sm text-zinc-400">Sin fianzas</span>
                          ) : (
                            <div className="flex flex-wrap items-center gap-1">
                              {r.vigentes > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">{r.vigentes} vig.</span>
                              )}
                              {r.vencidas > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{r.vencidas} venc.</span>
                              )}
                              {r.terminadas > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">{r.terminadas} term.</span>
                              )}
                              {r.reqPendientes > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">{r.reqPendientes} por emitir</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      {cExpanded && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={6} className="py-3 px-6">
                            <ContratoFianzasPanel
                              stats={r}
                              contratoId={c.id}
                              onFianzaClick={openFianzaFromPanel}
                              canUpload={false}
                              mode="client"
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="px-4 py-6 text-center">
              <Text className="text-sm text-zinc-400">Sin contratos en este proyecto</Text>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {accessibleProyectos.length === 0 && unassigned.length === 0 ? (
          <div className="text-center py-8">
            <Text>No hay proyectos en este convenio.</Text>
          </div>
        ) : (
          <>
            {accessibleProyectos.map((p) =>
              proyectoCard({
                key: p.id,
                nombre: p.nombre,
                pContracts: contractsByProject[p.id] || [],
              })
            )}
            {unassigned.length > 0 &&
              proyectoCard({
                key: '__sin_proyecto__',
                nombre: 'Sin proyecto',
                pContracts: unassigned,
              })}
          </>
        )}
      </div>
    );
  };

  // Hide the Proyectos tab when the convenio has none the user can see (and no
  // unassigned contracts to group) — same rule the broker page applies to
  // client viewers.
  const hasUnassigned = accessibleContracts.some((c) => !c.proyectoId);
  const visibleTabs = TABS.filter(
    (tab) => tab.key !== 'proyectos' || accessibleProyectos.length > 0 || hasUnassigned
  );

  return (
    <div>
      {/* No back arrow: convenio switching lives in the sidebar context
          selector, and the list route redirects to the active convenio. */}
      <ConvenioHeader convenio={convenio} beneficiarios={beneficiarios} />

      {/* Tabs */}
      <div className="overflow-x-auto mb-6 border-b border-azul-surelia/20">
        <div className="flex gap-1 min-w-max">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 sm:px-6 py-3 font-semibold focus:outline-none transition-all duration-200 border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key
                  ? 'text-azul-surelia border-azul-surelia bg-azul-surelia/5'
                  : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'resumen' && (
        <ConvenioResumen
          contracts={accessibleContracts}
          beneficiarios={beneficiarios}
          beneficiariosById={beneficiariosById}
          proyectosById={proyectosById}
          afianzadorasById={afianzadorasById}
          onProveedorClick={handleProveedorClickFromResumen}
          onFianzaClick={openFianza}
        />
      )}
      {activeTab === 'contratos' && renderContratos()}
      {activeTab === 'fianzas' && renderFianzasEmitidas()}
      {activeTab === 'proyectos' && renderProyectos()}
      {activeTab === 'proveedores' && (
        <ProveedoresChaseList
          proveedoresAgg={proveedoresAgg}
          mode="client"
          onContractClick={openContrato}
          expandKey={expandKey}
        />
      )}

      {/* ContratoView */}
      {selectedContrato && (
        <ContratoView
          contrato={selectedContrato}
          isOpen={isContratoViewOpen}
          onClose={() => { setIsContratoViewOpen(false); setSelectedContrato(null); }}
          convenio={convenio}
          beneficiariosById={beneficiariosById}
        />
      )}

      {/* FianzaView */}
      {selectedFianza && selectedFianzaContrato && (
        <FianzaView
          fianza={selectedFianza}
          contratoData={selectedFianzaContrato}
          convenio={convenio}
          beneficiariosById={beneficiariosById}
          isOpen={isFianzaViewOpen}
          onClose={() => {
            setIsFianzaViewOpen(false);
            setSelectedFianza(null);
            setSelectedFianzaContrato(null);
          }}
        />
      )}
    </div>
  );
};

export default ClientPortalConvenio;
